import React, { useEffect, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Select, Input, DatePicker, Table, Tooltip, Button, message, Menu, Dropdown } from 'antd';
import {
  TmsCarrierReceivablesProps,
  TmsCarrierReceivablesState,
  TmsCarrierReceivablesRecord,
  getAvailableActions,
} from 'src/components/pages/Receivables/Receivables.utils';
import { formatDateTime, formatNumber, transformName } from 'src/utils/utils';
import moment from 'moment';
import {
  ReloadOutlined,
  EllipsisOutlined,
  LeftOutlined,
  RightOutlined,
  VerticalLeftOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import BillingCyclePicker, {
  getDefaultDateRange,
} from 'src/components/pages/Receivables/components/BillingCyclePicker';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import RaiseInvoiceActionDrawer from 'src/components/pages/Receivables/Receivables.actionsDrawerGeneric';
import ResubmitInvoiceActionDrawer from 'src/components/pages/Receivables/Receivables.resubmitInvoiceActionDrawer';
import SettleOrRaiseDisputeInvoiceActionDrawer from 'src/components/pages/Receivables/Receivables.settleInvoiceActionDrawer';
import { fetchInvoiceData } from 'src/components/pages/Receivables/Receivables.actionsCreator';
import { downloadReceivablesInvoice } from 'src/components/pages/Receivables/Receivables.api';
import { useDebounce } from 'src/library/hooks/useDebounce';
import { TmsCarrierReceivablesStyles } from './Invoices.styles';
import { SET_RECEIVABLES_PAGINATION } from 'src/common/actions/constants';
import { downloadRequestRoute } from 'src/routing/routeConstants';
import { useHistory } from 'react-router-dom';
import { useGenericState } from 'src/library/UseGenericState';
import { get } from 'lodash';

interface RequestData {
  object_type: string;
  date_type: string;
  start_date?: string;
  end_date?: string;
  status_list?: string[];
  page_number?: number;
  limit?: number;
  invoice_number_list?: string[];
}

interface InvoiceBucketFilter {
  dateRange: [moment.Moment, moment.Moment];
  dateFilterType: 'event_date' | 'billing_cycle';
  invoiceNumber: string;
}

interface InvoiceBucketFilters {
  [key: string]: InvoiceBucketFilter;
}

interface InvoicesState extends Omit<TmsCarrierReceivablesState, 'bucketFilters'> {
  bucketFilters: InvoiceBucketFilters;
}

interface InvoicesProps extends TmsCarrierReceivablesProps {
  activeTab: string;
}

const dateFilterTypeOptions = [
  { label: 'Billing Cycle', value: 'billing_cycle' },
  { label: 'Created At', value: 'event_date' },
];

const TmsCarrierReceivables: React.FC<InvoicesProps> = ({ activeTab, ...props }) => {
  const { classes, tmsCarrierReceivables, setPagination, fetchInvoiceData } = props;
  const history = useHistory();

  const { pagination, invoiceBucketColumns } = tmsCarrierReceivables;

  const billingCycleConfig = {
    type: tmsCarrierReceivables.billingCycleType,
    start: tmsCarrierReceivables.billingCycleStart,
  };

  const [state, setState] = useGenericState<InvoicesState>({
    selectedRowKeys: [],
    searchFilterQuery: '',
    searching: false,
    searchFilterId: '',
    searchInvoiceNumberQuery: '',
    activeTabKey: 'all',
    isRaiseInvoiceActionDrawerVisible: false,
    isSettleInvoiceActionDrawerVisible: false,
    isResubmitInvoiceActionDrawerVisible: false,
    selectedRow: {},
    actionType: '',
    bucketFilters: {},
    isReadonly: false,
  });

  const {
    searchInvoiceNumberQuery,
    activeTabKey,
    isRaiseInvoiceActionDrawerVisible,
    isSettleInvoiceActionDrawerVisible,
    isResubmitInvoiceActionDrawerVisible,
    selectedRow,
    actionType,
    bucketFilters,
    isReadonly,
  } = state;

  const debouncedSearchInvoiceNumber = useDebounce(searchInvoiceNumberQuery, 500);

  const currentBucketColumns = useMemo(() => {
    return invoiceBucketColumns?.[activeTabKey] || [];
  }, [activeTabKey, invoiceBucketColumns]);

  const getDefaultFiltersForBucket = (
    dateFilterType: 'event_date' | 'billing_cycle' = 'billing_cycle',
  ): InvoiceBucketFilter => {
    const { startDate, endDate } = dateFilterType === 'billing_cycle'
      ? getDefaultDateRange(billingCycleConfig.type, billingCycleConfig.start)
      : getDefaultDateRange();

    return {
      dateRange: [startDate, endDate],
      dateFilterType,
      invoiceNumber: '',
    };
  };

  const updateBucketFilters = (
    tabKey: string,
    filterUpdates: Partial<InvoiceBucketFilter>,
    additionalState?: Partial<InvoicesState>,
  ) => {
    const currentFilters = bucketFilters[tabKey] || getDefaultFiltersForBucket();
    setState({
      ...additionalState,
      bucketFilters: {
        ...bucketFilters,
        [tabKey]: {
          ...currentFilters,
          ...filterUpdates,
        },
      },
    });
  };

  const buildRequestData = (currentFilters: InvoiceBucketFilter, tabKey: string): RequestData => ({
    object_type: 'vendor',
    date_type: currentFilters.dateFilterType || 'billing_cycle',
    start_date: currentFilters.dateRange[0].format('YYYY-MM-DD'),
    end_date: currentFilters.dateRange[1].format('YYYY-MM-DD'),
    invoice_number_list: debouncedSearchInvoiceNumber ? [debouncedSearchInvoiceNumber] : undefined,
    status_list: tabKey === 'all' ? undefined : [String(tabKey)],
    page_number: pagination.pageNumber,
    limit: pagination.resultPerPage,
  });

  const getCurrentBucketFilters = (): InvoiceBucketFilter => {
    if (!bucketFilters[activeTabKey]) {
      const defaultFilters = getDefaultFiltersForBucket();
      setState({
        bucketFilters: {
          ...bucketFilters,
          [activeTabKey]: defaultFilters,
        },
      });
      return defaultFilters;
    }
    return bucketFilters[activeTabKey];
  };

  const fetchViewDetailsAndCount = () => {
    const currentFilters = getCurrentBucketFilters();
    fetchInvoiceData(buildRequestData(currentFilters, activeTabKey));
  };

  useEffect(() => {
    const currentFilters = bucketFilters[activeTabKey];
    if (currentFilters?.dateRange?.length === 2) {
      fetchInvoiceData(buildRequestData(currentFilters, activeTabKey));
    }
  }, [
    activeTab,
    activeTabKey,
    bucketFilters[activeTabKey]?.dateRange?.[0],
    bucketFilters[activeTabKey]?.dateRange?.[1],
    bucketFilters[activeTabKey]?.dateFilterType,
    debouncedSearchInvoiceNumber,
    pagination.pageNumber,
    pagination.resultPerPage,
  ]);

  const handleDateRangeChange = (dates: [moment.Moment, moment.Moment]) => {
    if (moment(dates[1]).diff(moment(dates[0]), 'days') > 31) {
      message.error('Date range cannot be of more than 31 days');
      return;
    }
    updateBucketFilters(activeTabKey, { dateRange: dates });
  };

  const onBucketChange = (newTabKey: string) => {
    const existingFilters = bucketFilters[newTabKey];
    const filtersToUse = existingFilters || getDefaultFiltersForBucket();

    setState({ activeTabKey: newTabKey });

    if (!existingFilters) {
      updateBucketFilters(newTabKey, filtersToUse);
    }

    setPagination((prev) => ({
      ...prev,
      pageNumber: 1,
      resultPerPage: 10,
    }));
  };

  const handleDateFilterTypeChange = (val: 'event_date' | 'billing_cycle') => {
    const { startDate, endDate } = val === 'billing_cycle'
      ? getDefaultDateRange(billingCycleConfig.type, billingCycleConfig.start)
      : getDefaultDateRange();

    updateBucketFilters(
      activeTabKey,
      {
        dateFilterType: val,
        dateRange: [startDate, endDate],
      },
    );
  };

  const handleBillingCycleChange = (
    dateRange: { startDate: moment.Moment; endDate: moment.Moment } | null,
  ) => {
    if (!dateRange) return;

    const { startDate, endDate } = dateRange;
    updateBucketFilters(activeTabKey, {
      dateRange: [startDate, endDate],
    });
  };

  const renderTimeFilter = (): React.ReactElement => {
    const currentFilters = getCurrentBucketFilters();
    const currentDateFilterType = currentFilters.dateFilterType || 'billing_cycle';

    return (
      <div className={classes.timeFilterWrapper}>
        <Select
          onSelect={handleDateFilterTypeChange}
          value={currentDateFilterType}
          className={classes.select}
          disabled={tmsCarrierReceivables.loader}
          options={dateFilterTypeOptions}
        />
        {currentDateFilterType === 'event_date' ? (
          <div className={classes.datePickerWrapper}>
            <DatePicker.RangePicker
              format="DD MMM YYYY"
              className={classes.dateRangePicker}
              onChange={handleDateRangeChange}
              value={currentFilters.dateRange}
              ranges={{
                Today: [moment(), moment()],
                'Last 7 days': [moment().subtract(6, 'day'), moment()],
                'Last 30 days': [moment().subtract(29, 'day'), moment()],
                'This Month': [moment().startOf('month'), moment().endOf('month')],
              }}
              disabledDate={(current) => current && current > moment().endOf('day')}
              disabled={tmsCarrierReceivables.loader}
            />
          </div>
        ) : (
          <BillingCyclePicker
            cycleType={billingCycleConfig.type}
            cycleStart={billingCycleConfig.start}
            onChange={handleBillingCycleChange}
            minWidth="200px"
            value={{
              startDate: currentFilters.dateRange[0],
              endDate: currentFilters.dateRange[1],
            }}
          />
        )}
      </div>
    );
  };

  const renderSearchInvoiceNumber = (): React.ReactElement => (
    <div style={{ display: 'flex', alignItems: 'center', width: '300px', minWidth: '300px' }}>
      <span className={classes.filterLabel}>Invoice Number</span>
      <div className={classes.selectWrapper}>
        <Input
          allowClear
          className={classes.input}
          placeholder="Search Invoice Number"
          value={searchInvoiceNumberQuery}
          onChange={(e) => {
            setState({ searchInvoiceNumberQuery: e.target.value });
          }}
          style={{
            width: '100%',
            height: '32px',
          }}
        />
      </div>
    </div>
  );

  const handleDownload = async () => {
    const currentFilters = getCurrentBucketFilters();
    const requestData = {
      object_type: 'vendor',
      date_type: currentFilters.dateFilterType || 'billing_cycle',
      start_date: currentFilters.dateRange?.[0]?.format('YYYY-MM-DD') ?? '',
      end_date: currentFilters.dateRange?.[1]?.format('YYYY-MM-DD') ?? '',
      page_number: pagination.pageNumber,
      limit: pagination.resultPerPage,
    };

    const hideLoading = message.loading('Initiating download request...', 0);
    try {
      const response = await downloadReceivablesInvoice(requestData);

      if (response.isSuccess) {
        message.success('Download request submitted successfully');
        history.push(`${downloadRequestRoute}?dumpType=VENDOR_INVOICE`);
      } else {
        message.error(response.errorMessage || 'Failed to submit download request');
      }
    } catch (error) {
      message.error('An error occurred while submitting download request');
    } finally {
      hideLoading();
    }
  };

  const renderActionBar = () => (
    <div className={classes.actionBar}>
      <Tooltip title="Refresh">
        <Button
          icon={<ReloadOutlined style={{ fontSize: '16px' }} />}
          onClick={fetchViewDetailsAndCount}
          className={classes.iconButton}
        />
      </Tooltip>
      <Tooltip title="Download">
        <Button
          icon={<DownloadOutlined style={{ fontSize: '16px' }} />}
          onClick={handleDownload}
          className={classes.iconButton}
        />
      </Tooltip>
      <Button
        icon={<VerticalLeftOutlined style={{ fontSize: '10px', transform: 'rotate(180deg)' }} />}
        onClick={() => {
          setPagination({
            ...pagination,
            pageNumber: 1,
            nextOrPrev: 'first',
          });
        }}
        disabled={pagination.pageNumber === 1 || tmsCarrierReceivables.loader}
        className={classes.paginationButton}
      />
      <Button
        icon={<LeftOutlined style={{ fontSize: '10px' }} />}
        onClick={() => {
          setPagination({
            ...pagination,
            pageNumber: Math.max(1, pagination.pageNumber - 1),
            nextOrPrev: 'prev',
          });
        }}
        disabled={pagination.pageNumber === 1 || tmsCarrierReceivables.loader}
        className={classes.paginationButton}
      />
      <span className={classes.pageNumber}>{pagination.pageNumber}</span>
      <Button
        icon={<RightOutlined style={{ fontSize: '10px' }} />}
        onClick={() => {
          setPagination({
            ...pagination,
            pageNumber: pagination.pageNumber + 1,
            nextOrPrev: 'next',
          });
        }}
        disabled={!pagination.isNextPresent || tmsCarrierReceivables.loader}
        className={classes.paginationButton}
      />
      <Select
        className={classes.perPageSelect}
        onChange={(resultPerPage) => {
          setPagination({ ...pagination, resultPerPage, pageNumber: 1 });
        }}
        value={pagination.resultPerPage || 10}
      >
        <Select.Option value={10}>10 / Page</Select.Option>
        <Select.Option value={50}>50 / Page</Select.Option>
        <Select.Option value={100}>100 / Page</Select.Option>
      </Select>
    </div>
  );

  const handleInvoiceActionDrawer = (record, action, drawerType) => {
    setState({
      actionType: action,
      selectedRow: record,
      isRaiseInvoiceActionDrawerVisible: drawerType === 'raise',
      isResubmitInvoiceActionDrawerVisible: drawerType === 'resubmit',
      isSettleInvoiceActionDrawerVisible: drawerType === 'settle',
    });
  };

  const handleTmsCarrierInvoiceActions = (record, action) => {
    switch (action) {
      case 'raise_invoice':
        handleInvoiceActionDrawer(record, action, 'raise');
        break;
      case 'resubmit_invoice':
        handleInvoiceActionDrawer(record, action, 'resubmit');
        break;
      case 'settle_invoice':
      case 'settle_invoice_dispute':
        handleInvoiceActionDrawer(record, action, 'settle');
        break;
      case 'view_settled_invoice':
        setState({
          actionType: 'settle_invoice',
          selectedRow: record,
          isSettleInvoiceActionDrawerVisible: true,
          isReadonly: true,
        });
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (actionsList, record) => (
    <Menu>
      {actionsList.map((action) => (
        <Menu.Item
          key={action.action_id}
          onClick={() => handleTmsCarrierInvoiceActions(record, action.action_id)}
        >
          {action.pretty_name || action.action_id}
        </Menu.Item>
      ))}
    </Menu>
  );

  const renderActionColumn = (record) => {
    const availableActions = getAvailableActions(record.status);
    if (!Array.isArray(availableActions) || !availableActions.length) {
      return null;
    }

    return (
      <Dropdown overlay={getActionsMenu(availableActions, record)} trigger={['click']}>
        <EllipsisOutlined rotate={90} className={classes.ellipsisIcon} />
      </Dropdown>
    );
  };

  const getFieldValue = (item) => {
    switch (item.column_id) {
      case 'created_at':
      case 'raised_at':
      case 'approved_at':
        return (_, record) => formatDateTime(record[item.column_id], 'DD MMM YYYY', '-');
      case 'billing_cycle':
        return (_, record) => {
          if (record.start_date && record.end_date) {
            return `${formatDateTime(record.start_date, 'DD MMM YYYY', '-')} - ${formatDateTime(
              record.end_date,
              'DD MMM YYYY',
              '-',
            )}`;
          }
          return '-';
        };
      case 'net_amount':
      case 'total_tax':
      case 'final_amount':
        return (_, record) => formatNumber(record[item.column_id]);
      case 'actions':
        return (_, record) => renderActionColumn(record);
      case 'invoice_number':
        return (_, record) => get(record, item.column_id, '-') || '-';
      default:
        return (_, record) => transformName(get(record, item.column_id, '-') || '-');
    }
  };

  const generateColumnsToShow = useCallback(
    () =>
      currentBucketColumns.map((item) => ({
        title: item.pretty_name,
        dataIndex: item.column_id,
        key: item.column_id,
        width: item.width || 150,
        fixed: item.fixed as 'left' | 'right' | undefined,
        align: item.align as 'left' | 'right' | 'center' | undefined,
        render: getFieldValue(item),
      })),
    [currentBucketColumns, activeTabKey],
  );

  const renderFilters = () => (
    <div className={classes.filterContainer}>
      {renderTimeFilter()}
      {renderSearchInvoiceNumber()}
    </div>
  );

  const renderViewTable = () => (
    <div className={classes.tableWrapper}>
      <Table
        rowKey={(record: TmsCarrierReceivablesRecord) => record['id']}
        className={classes.table}
        dataSource={tmsCarrierReceivables.page_data || []}
        columns={generateColumnsToShow()}
        loading={tmsCarrierReceivables.loader}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );

  const renderTabsFromBuckets = () => (
    <div className={classes.tabsOuterWrapper}>
      <div className={classes.customTabsContainer}>
        {Object.entries(tmsCarrierReceivables?.invoiceBuckets || {}).map(([key, value]) => {
          const isActive = activeTabKey === key;
          return (
            <div
              key={key}
              className={classNames(classes.bucketTab, { [classes.bucketTabActive]: isActive })}
              onClick={() => onBucketChange(key)}
              role="tab"
              tabIndex={0}
              onKeyPress={(e) => e.key === 'Enter' && onBucketChange(key)}
            >
              <p
                className={classNames(classes.bucketTabText, {
                  [classes.bucketTabTextActive]: isActive,
                })}
              >
                {value as string}
              </p>
            </div>
          );
        })}
      </div>
      <div className={classes.tabContentContainer}>
        {renderFilters()}
        {renderActionBar()}
        {renderViewTable()}
      </div>
    </div>
  );

  return (
    <div className={classes.mainDiv}>
      {renderTabsFromBuckets()}
      <RaiseInvoiceActionDrawer
        actionType={actionType}
        isVisible={isRaiseInvoiceActionDrawerVisible}
        data={selectedRow}
        handleReload={fetchViewDetailsAndCount}
        columnsList={currentBucketColumns}
        handleClose={() => {
          setState({
            isRaiseInvoiceActionDrawerVisible: false,
            selectedRow: {},
          });
        }}
      />
      <ResubmitInvoiceActionDrawer
        actionType={actionType}
        isVisible={isResubmitInvoiceActionDrawerVisible}
        data={selectedRow}
        handleReload={fetchViewDetailsAndCount}
        columnsList={currentBucketColumns}
        handleClose={() => {
          setState({
            isResubmitInvoiceActionDrawerVisible: false,
            selectedRow: {},
          });
        }}
      />
      <SettleOrRaiseDisputeInvoiceActionDrawer
        actionType={actionType}
        isVisible={isSettleInvoiceActionDrawerVisible}
        data={selectedRow}
        handleReload={fetchViewDetailsAndCount}
        columnsList={currentBucketColumns}
        handleClose={() => {
          setState({
            isSettleInvoiceActionDrawerVisible: false,
            selectedRow: {},
            isReadonly: false,
          });
        }}
        isReadonly={isReadonly}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  tmsCarrierReceivables: state.receivables,
});

const mapDispatchToProps = (dispatch) => ({
  setPagination: (data) =>
    dispatch({
      type: SET_RECEIVABLES_PAGINATION,
      data,
    }),
  fetchInvoiceData: (data) => dispatch(fetchInvoiceData(data)),
});

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: TmsCarrierReceivablesStyles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(TmsCarrierReceivables);
